import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight, Star, Users, Zap, Globe, Mail, Phone, MapPin, Instagram, Linkedin, Search } from "lucide-react";
import { LanguageProvider, useLanguage } from "../contexts/LanguageContext";
import ThemeToggle from "../Components/ThemeToggle.js";
import LanguageToggle from "../Components/LanguageToggle";
import Loader from "../Components/Loader";
import AnniversaryAnimation from "../Components/AnniversaryAnimation";
import SearchOverlay from "../Components/SearchOverlay";

import Newsletter from "../Components/Newsletter";
import Cursor from "../Components/Cursor";
import HeroSection from "./sections/HeroSection";
import AboutSection from "./sections/AboutSection";
import ProjectsSection from "./sections/ProjectsSection";
import NewsSection from "./sections/NewsSection";
import CollaborationsSection from "./sections/CollaborationsSection";
import FilesSection from "./sections/FilesSection";
import ContactsSection from "./sections/ContactsSection";
import PrivacyPolicy from "./PrivacyPolicy";
import CookiePolicy from "./CookiePolicy";
import TermsOfService from "./TermsOfService";
import CookieConsent from "../Components/CookieConsent";

function HomeContent() {
  const { t } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState("hero");
  const [isDarkTheme, setIsDarkTheme] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [showAnniversary, setShowAnniversary] = useState(true);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showCookiePolicy, setShowCookiePolicy] = useState(false);
  const [showTermsOfService, setShowTermsOfService] = useState(false);

  const handleLoadingComplete = () => {
    setIsLoading(false);
  };

  const handleAnniversaryComplete = () => {
    setShowAnniversary(false);
  };

  const scrollToSection = (sectionId) => {
    setCurrentSection(sectionId);
    setIsMenuOpen(false);
  };

  const toggleTheme = () => {
    setIsDarkTheme(!isDarkTheme);
  };

  const pageVariants = {
    initial: {
      opacity: 0,
      x: "100vw",
    },
    animate: {
      opacity: 1,
      x: 0,
    },
    exit: {
      opacity: 0,
      x: "-100vw",
    },
  };

  const pageTransition = {
    type: "tween",
    ease: "anticipate",
    duration: 0.5
  };

  const menuItems = [
    { id: 'hero', label: t.menu.home, number: '01' },
    { id: 'about', label: t.menu.about, number: '02' },
    { id: 'collaborations', label: t.menu.collaborations, number: '03' },
    { id: 'projects', label: t.menu.projects, number: '04' },
    { id: 'news', label: t.menu.news, number: '05' },
    { id: 'files', label: t.menu.files, number: '06' },
    { id: 'contacts', label: t.menu.contacts, number: '07' }
  ];

  const services = [
    t.services.office,
    t.services.retail,
    t.services.hospitality,
    t.services.industrial,
    t.services.consulting
  ];

  const heroTitle = "We Build Extraordinary Digital Experiences";

  const heroAnimation = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const wordAnimation = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="overflow-x-hidden">
      <style>{`
        :root {
          --bg-primary: ${isDarkTheme ? '#101010' : '#F9F9F9'};
          --bg-secondary: ${isDarkTheme ? '#191919' : '#FFFFFF'};
          --text-primary: ${isDarkTheme ? '#EAEAEA' : '#222222'};
          --text-secondary: ${isDarkTheme ? '#A0A0A0' : '#666666'};
          --accent: #D4AF37;
          --accent-hover: #E4C04A;
          --border-color: ${isDarkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
        }
      `}</style>

      {/* Loader */}
      <AnimatePresence>
        {isLoading && (
          <Loader onComplete={handleLoadingComplete} />
        )}
      </AnimatePresence>

      {/* 40th Anniversary Animation */}
      <AnimatePresence>
        {!isLoading && showAnniversary && (
          <AnniversaryAnimation onComplete={handleAnniversaryComplete} />
        )}
      </AnimatePresence>

      {!isLoading && !showAnniversary && (
        <div className="bg-[--bg-primary] text-[--text-primary] transition-colors duration-500">
          <ThemeToggle isDarkTheme={isDarkTheme} toggleTheme={toggleTheme} />
          <LanguageToggle />

          {/* Search Button */}
          <motion.button
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.8, type: "spring", stiffness: 100 }}
            onClick={() => setIsSearchOpen(true)}
            className="fixed top-4 right-20 md:top-8 md:right-28 z-50 w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center bg-[--bg-secondary] shadow-md hover:bg-[--accent] transition-colors group"
            aria-label="Search"
          >
            <Search className="w-6 h-6 md:w-8 md:h-8 text-[--text-primary] group-hover:text-black transition-colors" />
          </motion.button>

          {/* Mobile Menu Button */}
          <motion.button
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1, type: "spring", stiffness: 100 }}
            onClick={() => setIsMenuOpen(true)}
            className="fixed top-4 right-4 md:top-8 md:right-8 z-50 w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center bg-[--bg-secondary] shadow-md hover:bg-[--accent] transition-colors group"
            aria-label="Menu"
          >
            <Menu className="w-8 h-8 md:w-10 md:h-10 text-[--text-primary] group-hover:text-black transition-colors" />
          </motion.button>

          {/* Fullscreen Menu */}
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="fixed inset-0 z-[100] bg-[--bg-primary]"
              >
                {/* Close Button */}
                <motion.button
                  initial={{ opacity: 0, rotate: -90 }}
                  animate={{ opacity: 1, rotate: 0 }}
                  exit={{ opacity: 0, rotate: 90 }}
                  transition={{ delay: 0.2 }}
                  onClick={() => setIsMenuOpen(false)}
                  className="absolute top-8 right-8 w-12 h-12 rounded-full flex items-center justify-center bg-[--accent] text-black hover:bg-[--accent-hover] transition-colors z-10"
                >
                  <X className="w-6 h-6" />
                </motion.button>

                {/* Menu Content Grid */}
                <div className="h-full grid grid-cols-1 lg:grid-cols-2 overflow-y-auto">
                  {/* Right Side - Additional Info */}
                  <div className="bg-[--bg-secondary] p-6 sm:p-8 md:p-12 lg:p-16 flex flex-col justify-between">

                    {/* Brand Section */}
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[--accent] mb-3 sm:mb-4">Ellegi</h1>
                      <p className="text-base sm:text-lg text-[--text-secondary] max-w-md mb-8 md:mb-0">
                        {t.menu.brandDesc}
                      </p>
                    </motion.div>

                    {/* Navigation Links */}
                    <motion.nav
                      className="flex-1 flex flex-col justify-center"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.2 }}
                    >
                      <ul className="space-y-6 md:space-y-4">
                        {menuItems.map((item, index) => (
                          <motion.li
                            key={item.id}
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.1 * index + 0.3 }}
                          >
                            <button
                              onClick={() => scrollToSection(item.id)}
                              className={`group flex items-center text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold transition-all duration-300 hover:text-[--accent] ${currentSection === item.id ? 'text-[--accent]' : 'text-[--text-primary]'
                                }`}
                            >
                              <span className="text-sm text-[--text-secondary] mr-6 font-mono group-hover:text-[--accent] transition-colors">
                                {item.number}
                              </span>
                              {item.label}
                            </button>
                          </motion.li>
                        ))}
                      </ul>
                    </motion.nav>

                    {/* Social Links */}
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.7 }}
                      className="flex items-center space-x-6 mt-10 md:mt-0"
                    >
                      <span className="text-sm text-[--text-secondary] uppercase tracking-wider">{t.menu.followUs}</span>
                      <div className="flex space-x-4">
                        <a href="https://www.instagram.com/ellegi_illuminazione/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-[--border-color] flex items-center justify-center text-[--text-secondary] hover:text-[--accent] hover:border-[--accent] transition-colors">
                          <Instagram className="w-4 h-4" />
                        </a>
                        <a href="https://www.linkedin.com/in/ellegi-srl-4b7917128/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-[--border-color] flex items-center justify-center text-[--text-secondary] hover:text-[--accent] hover:border-[--accent] transition-colors">
                          <Linkedin className="w-4 h-4" />
                        </a>
                      </div>
                    </motion.div>
                  </div>

                  {/* Left Side - Services & Contact */}
                  <div className="bg-gradient-to-b from-[--bg-primary] via-[--bg-secondary] to-[--bg-primary] flex flex-col justify-between p-8 md:p-16">

                    {/* Services Section */}
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                    >
                      <h3 className="text-xs sm:text-sm font-semibold text-[--accent] mb-2">{t.services.title}</h3>
                      <ul className="space-y-1 sm:space-y-2">
                        {services.map((service, index) => (
                          <motion.li
                            key={service}
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.1 * index + 0.5 }}
                            className="text-lg text-[--text-secondary] hover:text-[--accent] transition-colors cursor-pointer border-b border-[--border-color] pb-2"
                            onClick={() => scrollToSection('projects')}
                          >
                            {service}
                          </motion.li>
                        ))}
                      </ul>
                    </motion.div>

                    {/* Contact Info */}
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6 }}
                      className="space-y-6 sm:space-y-8 mt-12 md:mt-8"
                    >
                      <div>
                        <h3 className="text-xs sm:text-sm font-semibold text-[--accent] mb-2">{t.contact.title}</h3>
                        <div className="space-y-4">
                          <a
                            href="mailto:comm@lg-mi.it"
                            className="flex items-center text-[--text-primary] hover:text-[--accent] transition-colors group"
                          >
                            <Mail className="w-5 h-5 mr-3 text-[--text-secondary] group-hover:text-[--accent] transition-colors" />
                            <span className="text-lg">comm@lg-mi.it</span>
                          </a>
                          <a
                            href="tel:+390228957411"
                            className="flex items-center text-[--text-primary] hover:text-[--accent] transition-colors group"
                          >
                            <Phone className="w-5 h-5 mr-3 text-[--text-secondary] group-hover:text-[--accent] transition-colors" />
                            <span className="text-lg">+39 02 2895741</span>
                          </a>
                          <div className="flex items-center text-[--text-secondary]">
                            <MapPin className="w-5 h-5 mr-3 text-[--text-secondary]" />
                            <span>{t.contact.location}</span>
                          </div>
                        </div>
                      </div>

                      {/* Social Links */}
                      <div className="flex items-center space-x-4 pt-6 sm:pt-8 border-t border-[--border-color]">
                        <a href="https://www.instagram.com/ellegi_illuminazione/" target="_blank" rel="noopener noreferrer" className="text-[--text-secondary] hover:text-[--accent] transition-colors" aria-label="Instagram">
                          <Instagram className="w-4 h-4 sm:w-5 sm:h-5" />
                        </a>
                        <a href="https://www.linkedin.com/in/ellegi-srl-4b7917128/" target="_blank" rel="noopener noreferrer" className="text-[--text-secondary] hover:text-[--accent] transition-colors" aria-label="LinkedIn">
                          <Linkedin className="w-4 h-4 sm:w-5 sm:h-5" />
                        </a>
                      </div>

                      {/* CTA Button */}
                      <button
                        onClick={() => scrollToSection('contacts')}
                        className="bg-[--accent] hover:bg-[--accent-hover] text-black px-8 py-3 rounded-md font-semibold transition-all inline-flex items-center group"
                      >
                        {t.menu.contactUs}
                        <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                      </button>
                    </motion.div>
                  </div>
                </div>

                {/* Menu Footer */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                  className="absolute bottom-8 left-8 right-8 hidden md:flex justify-between items-center text-sm text-[--text-secondary]"
                >
                  <span>{t.footer.copyright}</span>
                  <span>{t.footer.tagline}</span>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <main className="h-screen">
            <AnimatePresence mode="wait">
              {currentSection === "hero" && (
                <motion.div
                  key="hero"
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  variants={pageVariants}
                  transition={pageTransition}
                  className="absolute w-full h-full"
                >
                  <HeroSection onNavigate={scrollToSection} />
                </motion.div>
              )}

              {currentSection === "about" && (
                <motion.div
                  key="about"
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  variants={pageVariants}
                  transition={pageTransition}
                  className="absolute w-full h-full"
                >
                  <AboutSection
                    onNavigate={scrollToSection}
                    onOpenPrivacyPolicy={() => setShowPrivacyPolicy(true)}
                    onOpenCookiePolicy={() => setShowCookiePolicy(true)}
                    onOpenTermsOfService={() => setShowTermsOfService(true)}
                  />
                </motion.div>
              )}

              {currentSection === "projects" && (
                <motion.div
                  key="projects"
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  variants={pageVariants}
                  transition={pageTransition}
                  className="absolute w-full h-full"
                >
                  <ProjectsSection
                    onNavigate={scrollToSection}
                    onOpenPrivacyPolicy={() => setShowPrivacyPolicy(true)}
                    onOpenCookiePolicy={() => setShowCookiePolicy(true)}
                    onOpenTermsOfService={() => setShowTermsOfService(true)}
                  />
                </motion.div>
              )}

              {currentSection === "news" && (
                <motion.div
                  key="news"
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  variants={pageVariants}
                  transition={pageTransition}
                  className="absolute w-full h-full"
                >
                  <NewsSection
                    onNavigate={scrollToSection}
                    onOpenPrivacyPolicy={() => setShowPrivacyPolicy(true)}
                    onOpenCookiePolicy={() => setShowCookiePolicy(true)}
                    onOpenTermsOfService={() => setShowTermsOfService(true)}
                  />
                </motion.div>
              )}

              {currentSection === "collaborations" && (
                <motion.div
                  key="collaborations"
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  variants={pageVariants}
                  transition={pageTransition}
                  className="absolute w-full h-full"
                >
                  <CollaborationsSection
                    onNavigate={scrollToSection}
                    onOpenPrivacyPolicy={() => setShowPrivacyPolicy(true)}
                    onOpenCookiePolicy={() => setShowCookiePolicy(true)}
                    onOpenTermsOfService={() => setShowTermsOfService(true)}
                  />
                </motion.div>
              )}

              {currentSection === "files" && (
                <motion.div
                  key="files"
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  variants={pageVariants}
                  transition={pageTransition}
                  className="absolute w-full h-full"
                >
                  <FilesSection
                    onNavigate={scrollToSection}
                    onOpenPrivacyPolicy={() => setShowPrivacyPolicy(true)}
                    onOpenCookiePolicy={() => setShowCookiePolicy(true)}
                    onOpenTermsOfService={() => setShowTermsOfService(true)}
                  />
                </motion.div>
              )}

              {currentSection === "contacts" && (
                <motion.div
                  key="contacts"
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  variants={pageVariants}
                  transition={pageTransition}
                  className="absolute w-full h-full"
                >
                  <ContactsSection
                    onNavigate={scrollToSection}
                    onOpenPrivacyPolicy={() => setShowPrivacyPolicy(true)}
                    onOpenCookiePolicy={() => setShowCookiePolicy(true)}
                    onOpenTermsOfService={() => setShowTermsOfService(true)}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </main>

          {/* Newsletter Component - Shows on all sections except hero */}
          {!isLoading && <Newsletter currentSection={currentSection} />}

          {/* Custom Cursor - Shows on all pages */}
          {!isLoading && <Cursor />}

          {/* Legal Documents Overlays */}
          <AnimatePresence>
            {showPrivacyPolicy && <PrivacyPolicy onClose={() => setShowPrivacyPolicy(false)} />}
            {showCookiePolicy && <CookiePolicy onClose={() => setShowCookiePolicy(false)} />}
            {showTermsOfService && <TermsOfService onClose={() => setShowTermsOfService(false)} />}
          </AnimatePresence>

          {/* Cookie Consent Banner */}
          {!isLoading && !showAnniversary && (
            <CookieConsent
              onOpenCookiePolicy={() => setShowCookiePolicy(true)}
              onOpenPrivacyPolicy={() => setShowPrivacyPolicy(true)}
            />
          )}

          <SearchOverlay
            isOpen={isSearchOpen}
            onClose={() => setIsSearchOpen(false)}
            onNavigate={scrollToSection}
          />
        </div>
      )}
    </div>
  );
}

export default function Home() {
  return (
    <LanguageProvider>
      <HomeContent />
    </LanguageProvider>
  );
}